import React, { useEffect, useState } from "react";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";
import CustomAlert from "../../helpers/CustomAlert";

// Mail status and resend for an LMBP, franchise, hub or user (Operation only).
// Lists the latest mails sent to the account's addresses and re-queues the last one.

const STATUS_STYLE = {
  Sent: { color: "#16a34a", background: "#dcfce7" },
  Queued: { color: "#b45309", background: "#fef3c7" },
  Failed: { color: "#dc2626", background: "#fee2e2" },
};

const EmailStatusModal = ({ code, title, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosPostApi(apiList.EMAIL_STATUS, { code });
      if (res?.data?.respCode === "00") {
        setData(res.data.data);
      } else {
        setData(null);
        setError(res?.data?.message || "Could not load mail status.");
      }
    } catch (e) {
      setError("Could not load mail status. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const resendLast = async () => {
    setResending(true);
    try {
      const res = await axiosPostApi(apiList.EMAIL_RESEND_LAST, { code });
      const ok = res?.data?.respCode === "00";
      CustomAlert({
        title: ok ? "Mail queued" : "Could not resend",
        text: res?.data?.message || (ok ? "" : "Please try again."),
        icon: ok ? "success" : "error",
        confirmButtonText: "OK",
      });
      if (ok) load();
    } catch (e) {
      CustomAlert({
        title: "Could not resend",
        text: "Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setResending(false);
    }
  };

  const mails = data?.mails || [];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Email status"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 1055, // below SweetAlert (1060) so alerts show on top
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white"
        style={{
          borderRadius: "14px",
          width: "min(900px, 100%)",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          className="d-flex justify-content-between align-items-start"
          style={{ padding: "16px 20px", borderBottom: "1px solid #e5e7eb", gap: "12px" }}
        >
          <div style={{ minWidth: 0 }}>
            <h5 className="mb-1">Email status</h5>
            <div style={{ fontSize: "13px", color: "#6b7280", overflowWrap: "anywhere" }}>
              {title} ({code})
              {data?.emails?.length ? ` · ${data.emails.join(", ")}` : ""}
            </div>
          </div>
          <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
        </div>

        <div style={{ padding: "12px 20px", overflow: "auto" }}>
          {loading && <div className="text-muted py-3">Loading…</div>}
          {!loading && error && <div style={{ color: "#dc2626" }}>{error}</div>}
          {!loading && !error && mails.length === 0 && (
            <div className="text-muted py-3">No mails sent to this account yet.</div>
          )}
          {!loading && mails.length > 0 && (
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0" style={{ fontSize: "13px" }}>
                <thead>
                  <tr>
                    <th>Queued at</th>
                    <th>Subject</th>
                    <th>To / CC</th>
                    <th>Status</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {mails.map((m) => (
                    <tr key={m.id}>
                      <td style={{ whiteSpace: "nowrap" }}>{m.createdDate || "-"}</td>
                      <td>
                        {m.subject || "-"}
                        {m.remark && (
                          <div style={{ color: "#6b7280", fontSize: "12px" }}>{m.remark}</div>
                        )}
                      </td>
                      <td style={{ overflowWrap: "anywhere" }}>
                        {m.sendTo}
                        {m.sendCc && (
                          <div style={{ color: "#6b7280", fontSize: "12px" }}>CC: {m.sendCc}</div>
                        )}
                      </td>
                      <td>
                        <span
                          title={m.errorMessage || `Status code: ${m.status || "-"}`}
                          style={{
                            ...STATUS_STYLE[m.statusLabel],
                            padding: "2px 8px",
                            borderRadius: "999px",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {m.statusLabel}
                        </span>
                        {m.errorMessage && (
                          <div style={{ color: "#dc2626", fontSize: "12px" }}>{m.errorMessage}</div>
                        )}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>{m.updatedDate || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div
          className="d-flex justify-content-end"
          style={{ padding: "12px 20px", borderTop: "1px solid #e5e7eb", gap: "10px" }}
        >
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="btn"
            style={{ background: "#FF702B", color: "#fff" }}
            disabled={loading || resending || mails.length === 0}
            onClick={resendLast}
          >
            {resending ? "Resending…" : "Resend last mail"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailStatusModal;
